import { Link, useNavigate } from "react-router-dom";
import { useState, useRef, useEffect } from "react";
import {
  ArrowRight,
  Sparkles,
  Star,
  MessageSquare,
  Youtube,
  Instagram,
  Crown,
  Mic,
  Send,
  Check,
  ChevronRight,
  Zap,
  BookOpen,
  Shield,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { PageLayout } from "@/components/layout/PageLayout";
import chefXMascot from "@/assets/chef-x-mascot.png";
import chefCatFace from "@/assets/chef-cat-face.png";

// ─── Demo Chat Simulation ───────────────────────────────────
// Zero-friction "try before you buy" — no login needed
const DEMO_SUGGESTIONS = [
  "I have chicken, rice, and broccoli",
  "Quick dinner with pasta and garlic",
  "What can I make with eggs and cheese?",
];

const DEMO_RESPONSES: Record<string, string> = {
  default: `😼 **Ooh, let me work with that!**

Here's what I'm thinking:
• **Garlic Chicken Stir-Fry** — fast, flavorful, done in 15 min
• **One-Pan Chicken & Rice Bake** — set it and forget it
• **Teriyaki Chicken Bowl** — because you deserve restaurant vibes at home

Pick one and I'll give you the full recipe. Trust the cat. 🐱`,
  pasta: `😼 **Pasta and garlic? Now we're talking.**

I've got options:
• **Aglio e Olio** — 4 ingredients, 10 minutes, pure Italian magic
• **Creamy Garlic Pasta** — add butter and parmesan if you've got 'em
• **Garlic Butter Shrimp Pasta** — if you want to impress someone

Which one speaks to your soul? 🍝`,
  eggs: `😼 **Eggs and cheese? Classic combo.**

Let's roll:
• **Fluffy French Omelette** — restaurant-style in 3 minutes
• **Cheesy Scrambled Eggs** — low and slow, creamy perfection
• **Croque Madame** — fancy grilled cheese with an egg hat

Say the word and I'll drop the full recipe. 🧀`,
};

function getDemoResponse(input: string): string {
  const lower = input.toLowerCase();
  if (lower.includes("pasta") || lower.includes("garlic")) return DEMO_RESPONSES.pasta;
  if (lower.includes("egg") || lower.includes("cheese")) return DEMO_RESPONSES.eggs;
  return DEMO_RESPONSES.default;
}

// ─── Social Proof Testimonials ──────────────────────────────
const TESTIMONIALS = [
  {
    quote: "Finally used my sad fridge leftovers and made something amazing!",
    name: "Sarah K.",
    detail: "Made a killer stir-fry from random veggies",
  },
  {
    quote: "The cat personality makes cooking actually fun. My kids love it.",
    name: "Marcus T.",
    detail: "Family dinner, every night",
  },
  {
    quote: "Better than asking ChatGPT because Chef X actually understands cooking.",
    name: "Priya M.",
    detail: "Home cook, recipe collector",
  },
];

export default function Home() {
  const navigate = useNavigate();
  const [videoStarted, setVideoStarted] = useState(false);

  // Demo chat state
  const [demoInput, setDemoInput] = useState("");
  const [demoMessages, setDemoMessages] = useState<
    Array<{ role: "user" | "assistant"; content: string }>
  >([]);
  const [demoTyping, setDemoTyping] = useState(false);
  const demoUsed = demoMessages.some((m) => m.role === "assistant");
  const demoChatRef = useRef<HTMLDivElement>(null);

  // Auto-scroll demo chat
  useEffect(() => {
    if (demoChatRef.current) {
      demoChatRef.current.scrollTop = demoChatRef.current.scrollHeight;
    }
  }, [demoMessages, demoTyping]);

  const handleDemoSend = (text?: string) => {
    const message = (text || demoInput).trim();
    if (!message || demoTyping) return;

    setDemoInput("");
    setDemoMessages((prev) => [...prev, { role: "user", content: message }]);
    setDemoTyping(true);

    // Simulate Chef X typing delay
    setTimeout(() => {
      const response = getDemoResponse(message);
      setDemoMessages((prev) => [...prev, { role: "assistant", content: response }]);
      setDemoTyping(false);
    }, 1200);
  };

  return (
    <PageLayout>
      {/* ─── Hero Section ─────────────────────────────────────── */}
      <section className="relative overflow-hidden min-h-[85vh] flex items-center">
        {/* Video Background */}
        <div className="absolute inset-0 -z-10">
          <div className="absolute inset-0 bg-background/80 z-10" />
          {videoStarted ? (
            <iframe
              src="https://www.youtube.com/embed/03gCdy55X24?autoplay=1&mute=1&loop=1&playlist=03gCdy55X24&controls=0&showinfo=0&rel=0&modestbranding=1&playsinline=1"
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[200%] h-[200%] pointer-events-none border-0"
              allow="autoplay; encrypted-media"
              title="Background video"
            />
          ) : (
            <button
              onClick={() => setVideoStarted(true)}
              className="absolute inset-0 w-full h-full bg-muted/50 cursor-default"
              aria-label="Load background video"
              tabIndex={-1}
            />
          )}
        </div>

        <div className="container py-12 md:py-20 relative z-10">
          <div className="mx-auto max-w-5xl">
            <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
              {/* Left: Hero Copy */}
              <div className="text-center md:text-left">
                {/* Cat mascot + personality badge */}
                <div className="flex items-center justify-center md:justify-start gap-3 mb-6">
                  <img
                    src={chefCatFace}
                    alt="Chef X"
                    className="w-16 h-16 rounded-full object-cover object-center drop-shadow-lg"
                  />
                  <Badge variant="secondary" className="text-sm px-3 py-1">
                    🐱 AI Chef with Attitude
                  </Badge>
                </div>

                {/* Main headline — personality-forward */}
                <h1 className="font-display text-3xl font-bold tracking-tight sm:text-4xl md:text-5xl text-foreground leading-tight">
                  Your Sassy Cat Chef{" "}
                  <span className="text-primary">Turns Fridge Scraps</span> into
                  Gourmet Meals
                </h1>

                {/* Subheadline */}
                <p className="mt-4 text-lg text-muted-foreground max-w-lg mx-auto md:mx-0">
                  Tell Chef X what you have. Get step-by-step recipes with
                  attitude, voice, and zero judgment about that sad leftover
                  situation. 😼
                </p>

                {/* Quick value props */}
                <div className="mt-6 flex flex-wrap gap-3 justify-center md:justify-start">
                  <span className="inline-flex items-center gap-1.5 text-sm text-muted-foreground bg-background/80 rounded-full px-3 py-1.5">
                    <Mic className="h-3.5 w-3.5 text-primary" />
                    Voice + Text
                  </span>
                  <span className="inline-flex items-center gap-1.5 text-sm text-muted-foreground bg-background/80 rounded-full px-3 py-1.5">
                    <Zap className="h-3.5 w-3.5 text-primary" />
                    Instant Recipes
                  </span>
                  <span className="inline-flex items-center gap-1.5 text-sm text-muted-foreground bg-background/80 rounded-full px-3 py-1.5">
                    <Shield className="h-3.5 w-3.5 text-primary" />
                    Food Safety Built-In
                  </span>
                </div>

                {/* CTAs */}
                <div className="mt-8 flex flex-col sm:flex-row items-center justify-center md:justify-start gap-3">
                  <Button
                    asChild
                    size="lg"
                    className="text-lg px-8 h-14 rounded-full w-full sm:w-auto"
                  >
                    <Link to="/chat">
                      Start Cooking Free
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </Link>
                  </Button>
                  <Button
                    asChild
                    variant="outline"
                    size="lg"
                    className="h-14 rounded-full bg-background/80 w-full sm:w-auto"
                  >
                    <Link to="/how-it-works">See How It Works</Link>
                  </Button>
                </div>

                {/* Trial badge */}
                <p className="mt-4 text-sm text-muted-foreground text-center md:text-left">
                  ✨ First 5 recipes free — no account needed
                </p>

                {/* Social Links with counts */}
                <div className="mt-6 flex items-center justify-center md:justify-start gap-3">
                  <a
                    href="https://youtube.com/@kitchenassistantx"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 rounded-full bg-background/80 px-3 py-1.5 text-sm font-medium text-muted-foreground hover:text-primary transition-colors"
                  >
                    <Youtube className="h-4 w-4" />
                    YouTube
                  </a>
                  <a
                    href="https://instagram.com/kitchenassistantx"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 rounded-full bg-background/80 px-3 py-1.5 text-sm font-medium text-muted-foreground hover:text-primary transition-colors"
                  >
                    <Instagram className="h-4 w-4" />
                    Instagram
                  </a>
                  <a
                    href="https://tiktok.com/@kitchenassistantx"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 rounded-full bg-background/80 px-3 py-1.5 text-sm font-medium text-muted-foreground hover:text-primary transition-colors"
                  >
                    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor">
                      <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1V9.01a6.28 6.28 0 00-.79-.05 6.28 6.28 0 106.28 6.28V9.4a8.16 8.16 0 004.82 1.58V7.53a4.85 4.85 0 01-1-.84z" />
                    </svg>
                    TikTok
                  </a>
                </div>
              </div>

              {/* Right: Demo Chat Box */}
              <div className="w-full max-w-md mx-auto md:mx-0">
                <div className="rounded-2xl border bg-card shadow-xl overflow-hidden">
                  {/* Chat header */}
                  <div className="bg-primary/10 px-4 py-3 flex items-center gap-2 border-b">
                    <img
                      src={chefCatFace}
                      alt="Chef X"
                      className="w-8 h-8 rounded-full object-cover object-center"
                    />
                    <div>
                      <p className="text-sm font-semibold">Chef X</p>
                      <p className="text-xs text-muted-foreground">
                        {demoTyping ? "Typing..." : "Online"}
                      </p>
                    </div>
                    <Badge variant="outline" className="ml-auto text-xs">
                      Try it free
                    </Badge>
                  </div>

                  {/* Messages area */}
                  <div
                    ref={demoChatRef}
                    className="h-64 overflow-y-auto p-4 space-y-3"
                  >
                    {demoMessages.length === 0 && (
                      <div className="text-center py-6">
                        <p className="text-sm text-muted-foreground mb-3">
                          Try asking Chef X something:
                        </p>
                        <div className="flex flex-col gap-2">
                          {DEMO_SUGGESTIONS.map((suggestion) => (
                            <button
                              key={suggestion}
                              onClick={() => handleDemoSend(suggestion)}
                              className="text-left text-sm bg-muted/50 hover:bg-muted rounded-xl px-3 py-2 transition-colors text-foreground"
                            >
                              "{suggestion}"
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {demoMessages.map((msg, i) => (
                      <div
                        key={i}
                        className={`flex ${
                          msg.role === "user" ? "justify-end" : "justify-start"
                        }`}
                      >
                        <div
                          className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm ${
                            msg.role === "user"
                              ? "bg-primary text-primary-foreground rounded-br-md"
                              : "bg-muted rounded-bl-md"
                          }`}
                        >
                          {msg.role === "assistant" ? (
                            <div className="whitespace-pre-wrap">
                              {msg.content.split(/(\*\*[^**]+\*\*)/g).map((part, j) => {
                                if (part.startsWith("**") && part.endsWith("**")) {
                                  return <strong key={j}>{part.slice(2, -2)}</strong>;
                                }
                                return part.replace(/• /g, "→ ");
                              })}
                            </div>
                          ) : (
                            msg.content
                          )}
                        </div>
                      </div>
                    ))}

                    {demoTyping && (
                      <div className="flex justify-start">
                        <div className="bg-muted rounded-2xl rounded-bl-md px-4 py-3">
                          <div className="flex gap-1">
                            <span className="w-2 h-2 bg-muted-foreground/50 rounded-full animate-bounce" />
                            <span
                              className="w-2 h-2 bg-muted-foreground/50 rounded-full animate-bounce"
                              style={{ animationDelay: "0.1s" }}
                            />
                            <span
                              className="w-2 h-2 bg-muted-foreground/50 rounded-full animate-bounce"
                              style={{ animationDelay: "0.2s" }}
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Input area */}
                  <div className="border-t p-3">
                    {demoUsed ? (
                      <Button
                        className="w-full rounded-full"
                        onClick={() => navigate("/chat")}
                      >
                        Continue with Chef X
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    ) : (
                      <form
                        onSubmit={(e) => {
                          e.preventDefault();
                          handleDemoSend();
                        }}
                        className="flex gap-2"
                      >
                        <Input
                          value={demoInput}
                          onChange={(e) => setDemoInput(e.target.value)}
                          placeholder="Tell Chef X what you have..."
                          className="rounded-full"
                          disabled={demoTyping}
                        />
                        <Button
                          type="submit"
                          size="icon"
                          className="rounded-full shrink-0"
                          disabled={!demoInput.trim() || demoTyping}
                        >
                          <Send className="h-4 w-4" />
                        </Button>
                      </form>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Free vs Pro Comparison ────────────────────────────── */}
      <section className="border-t bg-muted/30 py-16">
        <div className="container">
          <div className="text-center mb-10">
            <h2 className="font-display text-3xl font-bold mb-3">
              Free to Start. Pro When You're Ready.
            </h2>
            <p className="text-muted-foreground max-w-lg mx-auto">
              Chef X gives you a taste for free. Upgrade when you want
              unlimited recipes, saved favorites, and the cat's premium
              attitude.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">
            {/* Free Tier */}
            <div className="rounded-2xl border bg-card p-6">
              <div className="flex items-center gap-2 mb-4">
                <MessageSquare className="h-5 w-5 text-muted-foreground" />
                <h3 className="font-display text-lg font-semibold">Free</h3>
                <Badge variant="outline" className="ml-auto">
                  $0
                </Badge>
              </div>
              <ul className="space-y-3 text-sm">
                <li className="flex items-start gap-2">
                  <Check className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                  <span>5 recipe conversations per month</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                  <span>Voice and text input</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                  <span>Full Chef X personality</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                  <span>Food safety warnings</span>
                </li>
              </ul>
              <Button
                asChild
                variant="outline"
                className="w-full mt-6 rounded-full"
              >
                <Link to="/chat">Try Free</Link>
              </Button>
            </div>

            {/* Pro Tier */}
            <div className="rounded-2xl border-2 border-primary bg-card p-6 relative">
              <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 px-4">
                <Crown className="h-3 w-3 mr-1" />
                Most Popular
              </Badge>
              <div className="flex items-center gap-2 mb-4">
                <Star className="h-5 w-5 text-primary" />
                <h3 className="font-display text-lg font-semibold">Pro</h3>
                <Badge className="ml-auto">$4.99/mo</Badge>
              </div>
              <ul className="space-y-3 text-sm">
                <li className="flex items-start gap-2">
                  <Check className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                  <span>
                    <strong>Unlimited</strong> conversations
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                  <span>Save & organize recipes</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                  <span>Voice replies from Chef X</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                  <span>Priority support</span>
                </li>
              </ul>
              <Button asChild className="w-full mt-6 rounded-full">
                <Link to="/pricing">
                  Upgrade to Pro
                  <ChevronRight className="ml-1 h-4 w-4" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Social Proof ─────────────────────────────────────── */}
      <section className="py-16">
        <div className="container">
          <h2 className="font-display text-2xl font-bold text-center mb-8">
            What Home Cooks Are Saying
          </h2>
          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {TESTIMONIALS.map((t, i) => (
              <div
                key={i}
                className="rounded-2xl border bg-card p-5 flex flex-col"
              >
                <div className="flex gap-1 mb-3">
                  {[...Array(5)].map((_, j) => (
                    <Star
                      key={j}
                      className="h-4 w-4 fill-primary text-primary"
                    />
                  ))}
                </div>
                <p className="text-sm text-foreground mb-3 flex-1">
                  "{t.quote}"
                </p>
                <div className="text-xs text-muted-foreground">
                  <span className="font-medium">{t.name}</span> · {t.detail}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Bottom CTA ───────────────────────────────────────── */}
      <section className="border-t bg-muted/30 py-16">
        <div className="container text-center">
          <img
            src={chefXMascot}
            alt="Chef X mascot"
            className="w-24 h-auto mx-auto mb-6 drop-shadow-lg"
          />
          <h2 className="font-display text-3xl font-bold mb-3">
            Ready to Cook Something Amazing?
          </h2>
          <p className="text-muted-foreground mb-6 max-w-md mx-auto">
            Chef X is waiting. Tell the cat what's in your fridge, and prepare
            to be surprised. No sign-up required.
          </p>
          <Button
            asChild
            size="lg"
            className="text-lg px-10 h-14 rounded-full"
          >
            <Link to="/chat">
              Start Cooking Free
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </Button>
        </div>
      </section>
    </PageLayout>
  );
}
